/**
 * Account and Profile storage helpers using localStorage.
 * 
 * This provides a localStorage-backed implementation for MVP.
 * Later, this will be migrated to OPFS SQLite.
 */

import type { Account, Profile, Workspace, WorkspaceMember } from '@yada/types';

const STORAGE_PREFIX = 'yada:account:';
const ACCOUNT_KEY = 'account';
const PROFILES_KEY = 'profiles';
const ACTIVE_PROFILE_KEY = 'activeProfileId';
const WORKSPACES_KEY = 'workspaces';
const WORKSPACE_MEMBERS_KEY = 'workspaceMembers';
const SESSION_KEY = 'session';

// Helper to serialize/deserialize dates
function serializeAccount(account: Account): Record<string, unknown> {
  return {
    ...account,
    createdAt: account.createdAt instanceof Date ? account.createdAt.toISOString() : new Date(account.createdAt).toISOString(),
    serverSynced: account.serverSynced || false,
    isAccountless: account.isAccountless || false,
    emailVerified: account.emailVerified !== undefined ? account.emailVerified : true,
  };
}

function deserializeAccount(data: Record<string, unknown>): Account {
  return {
    ...data,
    createdAt: new Date((data.createdAt || data.created_at) as string),
    serverSynced: data.serverSynced === true || data.serverSynced === 'true' || data.server_synced === true,
    isAccountless: data.isAccountless === true || data.isAccountless === 'true',
    emailVerified: data.emailVerified !== undefined ? (data.emailVerified === true || data.emailVerified === 'true') : (data.email_verified !== undefined ? (data.email_verified === true || data.email_verified === 'true') : true),
  } as Account;
}

function serializeProfile(profile: Profile): Record<string, unknown> {
  return {
    ...profile,
    accountId: profile.accountId,
    profileType: profile.profileType,
    createdAt: profile.createdAt instanceof Date ? profile.createdAt.toISOString() : new Date(profile.createdAt).toISOString(),
    updatedAt: profile.updatedAt instanceof Date ? profile.updatedAt.toISOString() : new Date(profile.updatedAt).toISOString(),
  };
}

function deserializeProfile(data: Record<string, unknown>): Profile {
  return {
    ...data,
    id: data.id as string,
    accountId: (data.accountId || data.account_id || null) as string | null,
    profileType: (data.profileType || data.profile_type || 'standard') as Profile['profileType'],
    name: (data.name as string) || '',
    verificationLevel: typeof data.verificationLevel === 'number'
      ? data.verificationLevel
      : (typeof data.verification_level === 'number'
        ? data.verification_level
        : parseInt((data.verification_level || data.verificationLevel || '0') as string, 10) || 0),
    visibility: (data.visibility || 'public') as Profile['visibility'],
    metadata: (data.metadata || {}) as Record<string, unknown>,
    createdAt: new Date((data.createdAt || data.created_at) as string),
    updatedAt: new Date((data.updatedAt || data.updated_at) as string),
  } as Profile;
}

function serializeWorkspace(workspace: Workspace): Record<string, unknown> {
  return {
    ...workspace,
    createdAt: workspace.createdAt.toISOString(),
    updatedAt: workspace.updatedAt.toISOString(),
  };
}

function deserializeWorkspace(data: Record<string, unknown>): Workspace {
  return {
    ...data,
    createdAt: new Date(data.createdAt as string),
    updatedAt: new Date(data.updatedAt as string),
  } as Workspace;
}

function serializeWorkspaceMember(member: WorkspaceMember): Record<string, unknown> {
  return {
    ...member,
    createdAt: member.createdAt.toISOString(),
    updatedAt: member.updatedAt.toISOString(),
  };
}

function deserializeWorkspaceMember(data: Record<string, unknown>): WorkspaceMember {
  return {
    ...data,
    createdAt: new Date(data.createdAt as string),
    updatedAt: new Date(data.updatedAt as string),
  } as WorkspaceMember;
}

// Account storage
export async function getAccount(): Promise<Account | null> {
  const raw = localStorage.getItem(STORAGE_PREFIX + ACCOUNT_KEY);
  if (!raw) return null;
  try {
    const data = JSON.parse(raw) as Record<string, unknown>;
    return deserializeAccount(data);
  } catch {
    return null;
  }
}

export async function setAccount(account: Account): Promise<void> {
  localStorage.setItem(STORAGE_PREFIX + ACCOUNT_KEY, JSON.stringify(serializeAccount(account)));
}

export async function removeAccount(): Promise<void> {
  localStorage.removeItem(STORAGE_PREFIX + ACCOUNT_KEY);
}

// Profile storage
export async function getProfile(id: string): Promise<Profile | null> {
  const profiles = await getProfiles();
  return profiles.find(p => p.id === id) || null;
}

function orderProfilesWithMainFirst(profileList: Profile[]): Profile[] {
  if (profileList.length <= 1) return profileList;
  const householdIdx = profileList.findIndex(p => p.profileType === 'household');
  if (householdIdx === -1) return profileList;

  let mainIdx = profileList.findIndex(p => p.profileType === 'standard');
  if (mainIdx === -1) mainIdx = profileList.findIndex(p => p.profileType === 'internal');
  if (mainIdx === -1) mainIdx = profileList.findIndex(p => p.profileType !== 'household');
  if (mainIdx === -1 || householdIdx >= mainIdx) return profileList;

  const result = [...profileList];
  const [mainProfile] = result.splice(mainIdx, 1);
  result.splice(householdIdx, 0, mainProfile);
  return result;
}

export async function getProfiles(): Promise<Profile[]> {
  const raw = localStorage.getItem(STORAGE_PREFIX + PROFILES_KEY);
  if (!raw) return [];
  try {
    const data = JSON.parse(raw) as Record<string, unknown>[];
    return orderProfilesWithMainFirst(data.map(deserializeProfile));
  } catch {
    return [];
  }
}

export async function getProfilesForAccount(accountId: string): Promise<Profile[]> {
  const profiles = await getProfiles();
  return profiles.filter(p => p.accountId === accountId);
}

export async function setProfiles(profiles: Profile[]): Promise<void> {
  localStorage.setItem(STORAGE_PREFIX + PROFILES_KEY, JSON.stringify(profiles.map(serializeProfile)));
}

export async function reorderProfiles(orderedIds: string[]): Promise<void> {
  const current = await getProfiles();
  const idMap = new Map(current.map(p => [p.id, p]));
  const ordered: Profile[] = [];
  for (const id of orderedIds) {
    const p = idMap.get(id);
    if (p) {
      ordered.push(p);
      idMap.delete(id);
    }
  }
  for (const p of idMap.values()) {
    ordered.push(p);
  }
  await setProfiles(ordered);
}

export async function setProfile(profile: Profile): Promise<void> {
  const profiles = await getProfiles();
  const index = profiles.findIndex(p => p.id === profile.id);
  if (index >= 0) {
    profiles[index] = profile;
  } else {
    profiles.push(profile);
  }
  localStorage.setItem(STORAGE_PREFIX + PROFILES_KEY, JSON.stringify(profiles.map(serializeProfile)));
}

export async function removeProfile(id: string): Promise<void> {
  const profiles = await getProfiles();
  const filtered = profiles.filter(p => p.id !== id);
  localStorage.setItem(STORAGE_PREFIX + PROFILES_KEY, JSON.stringify(filtered.map(serializeProfile)));
}

export async function getActiveProfileId(): Promise<string | null> {
  const raw = localStorage.getItem(STORAGE_PREFIX + ACTIVE_PROFILE_KEY);
  return raw || null;
}

export async function setActiveProfileId(profileId: string | null): Promise<void> {
  if (profileId) {
    localStorage.setItem(STORAGE_PREFIX + ACTIVE_PROFILE_KEY, profileId);
  } else {
    localStorage.removeItem(STORAGE_PREFIX + ACTIVE_PROFILE_KEY);
  }
}

// Workspace storage
export async function getWorkspace(id: string): Promise<Workspace | null> {
  const workspaces = await getWorkspaces();
  return workspaces.find(w => w.id === id) || null;
}

export async function getWorkspaces(): Promise<Workspace[]> {
  const raw = localStorage.getItem(STORAGE_PREFIX + WORKSPACES_KEY);
  if (!raw) return [];
  try {
    const data = JSON.parse(raw) as Record<string, unknown>[];
    return data.map(deserializeWorkspace);
  } catch {
    return [];
  }
}

export async function setWorkspace(workspace: Workspace): Promise<void> {
  const workspaces = await getWorkspaces();
  const index = workspaces.findIndex(w => w.id === workspace.id);
  if (index >= 0) {
    workspaces[index] = workspace;
  } else {
    workspaces.push(workspace);
  }
  localStorage.setItem(STORAGE_PREFIX + WORKSPACES_KEY, JSON.stringify(workspaces.map(serializeWorkspace)));
}

export async function removeWorkspace(id: string): Promise<void> {
  const workspaces = await getWorkspaces();
  const filtered = workspaces.filter(w => w.id !== id);
  localStorage.setItem(STORAGE_PREFIX + WORKSPACES_KEY, JSON.stringify(filtered.map(serializeWorkspace)));
}

// Workspace member storage
export async function getWorkspaceMembers(workspaceId: string): Promise<WorkspaceMember[]> {
  const raw = localStorage.getItem(STORAGE_PREFIX + WORKSPACE_MEMBERS_KEY);
  if (!raw) return [];
  try {
    const data = JSON.parse(raw) as Record<string, unknown>[];
    return data
      .map(deserializeWorkspaceMember)
      .filter(m => m.workspaceId === workspaceId);
  } catch {
    return [];
  }
}

export async function setWorkspaceMember(member: WorkspaceMember): Promise<void> {
  const raw = localStorage.getItem(STORAGE_PREFIX + WORKSPACE_MEMBERS_KEY);
  let members: WorkspaceMember[] = [];
  if (raw) {
    try {
      const data = JSON.parse(raw) as Record<string, unknown>[];
      members = data.map(deserializeWorkspaceMember);
    } catch {
      // Ignore parse errors, start fresh
    }
  }
  
  const index = members.findIndex(
    m => m.workspaceId === member.workspaceId && m.profileId === member.profileId
  );
  if (index >= 0) {
    members[index] = member;
  } else {
    members.push(member);
  }
  localStorage.setItem(STORAGE_PREFIX + WORKSPACE_MEMBERS_KEY, JSON.stringify(members.map(serializeWorkspaceMember)));
}

export async function removeWorkspaceMember(workspaceId: string, profileId: string): Promise<void> {
  const raw = localStorage.getItem(STORAGE_PREFIX + WORKSPACE_MEMBERS_KEY);
  if (!raw) return;
  try {
    const data = JSON.parse(raw) as Record<string, unknown>[];
    const members = data
      .map(deserializeWorkspaceMember)
      .filter(m => !(m.workspaceId === workspaceId && m.profileId === profileId));
    localStorage.setItem(STORAGE_PREFIX + WORKSPACE_MEMBERS_KEY, JSON.stringify(members.map(serializeWorkspaceMember)));
  } catch {
    // Ignore errors
  }
}

// Session storage
export async function getSessionToken(): Promise<string | null> {
  const raw = localStorage.getItem(STORAGE_PREFIX + SESSION_KEY) || localStorage.getItem('yada:access_token');
  return raw || null;
}

export async function setSessionToken(token: string): Promise<void> {
  localStorage.setItem(STORAGE_PREFIX + SESSION_KEY, token);
  localStorage.setItem('yada:access_token', token);
}

export async function removeSessionToken(): Promise<void> {
  localStorage.removeItem(STORAGE_PREFIX + SESSION_KEY);
  localStorage.removeItem('yada:access_token');
  localStorage.removeItem('yada:access_token_expires_at');
}

